import { CommonModule } from '@angular/common';
import { Component, ChangeDetectionStrategy, inject } from '@angular/core';
import { Router } from '@angular/router';
import { FieldTypeConfig, FieldType } from '@ngx-formly/core';

@Component({
  selector: "formly-field-template",
  standalone: true,
  templateUrl: "./template.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule],
})
export class FormlyFieldTemplate extends FieldType<FieldTypeConfig> {

    private readonly router = inject(Router);

    override defaultOptions = {
      props: {
        template: undefined,
      },
    };

    getTemplateContent(): string {
      const template = this.props.template;
      if (typeof template === 'function') {
        return template(this.model);
      }
      return template;
    }

    /**
     * Route in-app links through the Angular Router rather than letting the
     * browser perform a full document navigation. Template markup is rendered via
     * [innerHTML], so `routerLink` is never compiled — a raw `<a href="/x">` would
     * hard-navigate and bypass route guards like canDeactivate(), silently
     * discarding unsaved form edits (CHAT-505). We intercept left-clicks on
     * same-origin anchors here and hand the path to the router so guards run.
     * External URLs, new-tab/modifier clicks, downloads, and target="_blank"
     * anchors fall through to default browser behavior.
     */
    onTemplateClick(event: MouseEvent): void {
      const anchor = (event.target as HTMLElement | null)?.closest('a');
      if (!anchor) {
        return;
      }

      if (
        event.button !== 0 ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        event.altKey ||
        anchor.target === '_blank' ||
        anchor.hasAttribute('download')
      ) {
        return;
      }

      // Only intercept relative, same-origin app paths (e.g. "/connectors").
      // Skip external URLs, protocol-relative ("//host"), and special schemes.
      const href = anchor.getAttribute('href');
      if (!href || !href.startsWith('/') || href.startsWith('//')) {
        return;
      }

      event.preventDefault();
      this.router.navigateByUrl(href);
    }

}